/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { Field, Formik } from "formik";
import * as Yup from "yup";

import {
  ActionButton,
  CommandBar,
  DefaultButton,
  ICommandBarItemProps,
  IPersonaProps,
  IPickerItemProps,
  Label,
  MessageBar,
  MessageBarType,
  NormalPeoplePicker,
  PeoplePickerItem,
  PersonaPresence,
  PrimaryButton,
  Separator,
  Stack,
  Text,
  ValidationState,
} from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import { IPersonaPropsExtend } from "Components/AccessControlUserForm/IPersonaPropsExtend";
import FormikErrorMessage from "Components/Common/FormikErrorMessage/FormikErrorMessage";
import {
  bodyContainerStackStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { IUserDetailsGraphProps } from "Components/DistributionLists/IUserDetailsGraphProps";
import MicrosoftMembersList from "Components/MicrosoftMembersList/MicrosoftMembersList";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { messageBarStyles } from "Components/SidePanel/SidePanel.styles";
import { OperationStatus } from "Constants/OperationStatus";
import { peoplePickerSuggestionProps } from "Constants/PeoplePickerConstants";
import { MICROSOFT_DOMAIN_REGEX } from "Constants/RegexConstants";
import { PermissionsEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  containerStackTokens,
  wrapperStackStyles,
} from "Containers/MVP/MostValuableProfessionalDetailsContainer/MostValuableProfessionalDetailsContainer.styles";
import {
  addMicrosoftMemberToDL,
  fetchMicrosoftMembersForDL,
  removeMicrosoftMemberFromDL,
  resetMicrosoftMemberDLs,
} from "Dataflows/DistributionLists/DistributionListsAction";
import {
  getAddMemberToDLErrorMessage,
  getNetworkOperationStatusAddMember,
  getNetworkOperationStatusRemoveMember,
  getRemoveMemberFromDLErrorMessage,
  getSelectedDistributionList,
  getSelectedDistributionListMicrosoftMembers,
} from "Dataflows/DistributionLists/DistributionListsSelector";
import { callMsGraphGetUserByEmail } from "Utility/MsGraphApiCall";
import { onInputChange } from "Utility/Utils";

import {
  commandBarStyles,
  fieldItemStyles,
  flexStackStyles,
  footerButtonStyles,
  formStackStyles,
  horizontalStackTokens,
  verticalStackTokens,
  ContentContainer,
  FlexWrapper,
  PageHeader,
  PanelSubHeader,
  StyledForm,
  Subtitle,
  Title,
} from "./DistributionListDetails.styles";

/**
 * Distribution List Details functional component
 * @param props {IDistributionListDetailsProps} Distribution List Details props.
 * @returns {React.ReactElement} Distribution List Details Component.
 */
const DistributionListDetails = (): React.ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { emailAlias, tenant } = useParams<{ emailAlias: string; tenant: string }>();
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [isAddingUser, setIsAddingUser] = useState<boolean>(false);
  const [isRemovingUser, setIsRemovingUser] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<IUserDetailsGraphProps | undefined>(undefined);
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isAddingOrRemoving, setIsAddingOrRemoving] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const selectedDistributionList = useSelector(getSelectedDistributionList);
  const networkOperationStatusAddMember = useSelector(getNetworkOperationStatusAddMember);
  const networkOperationStatusRemoveMember = useSelector(getNetworkOperationStatusRemoveMember);
  const addUserErrorMessage = useSelector(getAddMemberToDLErrorMessage);
  const removeUserErrorMessage = useSelector(getRemoveMemberFromDLErrorMessage);
  const microsoftMembers = useSelector(getSelectedDistributionListMicrosoftMembers);
  const { hasPermission } = useAuth();
  const canUpdateDistributionList =
    (tenant.toLowerCase() === TenantTypesEnum.MVP.toLowerCase() &&
      hasPermission(TenantTypesEnum.MVP, PermissionsEnum.MVP_UPDATE_DISTRIBUTIONS_LIST)) ||
    (tenant.toLowerCase() === TenantTypesEnum.RD.toLowerCase() &&
      hasPermission(TenantTypesEnum.RD, PermissionsEnum.RD_UPDATE_BENEFITS));

  useEffect(() => {
    dispatch(fetchMicrosoftMembersForDL(tenant, emailAlias));
  }, []);

  useEffect(() => {
    if (isPanelOpen) {
      dispatch(resetMicrosoftMemberDLs());
    }
  }, [isPanelOpen]);

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
  };

  const cleanup = (): void => {
    setIsAddingOrRemoving(false);
    setUserDetails(undefined);
    dispatch(fetchMicrosoftMembersForDL(tenant, selectedDistributionList.distributionListEmail));
    setIsSubmitting(false);
    dismissPanel();
  };

  useEffect(() => {
    if (networkOperationStatusAddMember === OperationStatus.Succeeded) {
      setSuccessMessage(
        "Your request was successful! Please allow 30 minutes for Exchange to process the changes."
      );
      cleanup();
    } else if (networkOperationStatusAddMember === OperationStatus.Pending) {
      setIsAddingOrRemoving(true);
    } else if (networkOperationStatusAddMember === OperationStatus.Failed) {
      setIsAddingOrRemoving(false);
      setIsSubmitting(false);
    }
  }, [networkOperationStatusAddMember]);

  useEffect(() => {
    if (networkOperationStatusRemoveMember === OperationStatus.Succeeded) {
      setSuccessMessage(
        "Your request was successful! Please allow 30 minutes for Exchange to process the changes."
      );
      cleanup();
    } else if (networkOperationStatusRemoveMember === OperationStatus.Pending) {
      setIsAddingOrRemoving(true);
    } else if (networkOperationStatusRemoveMember === OperationStatus.Failed) {
      setIsAddingOrRemoving(false);
      setIsSubmitting(false);
    }
  }, [networkOperationStatusRemoveMember]);

  const picker = React.useRef(null);

  /**
   * Handler for when the back button is clicked.
   * @returns {void} void no return for function.
   */
  const onBackClickEvent = (): void => {
    history.goBack();
  };

  const items: ICommandBarItemProps[] = [
    {
      key: "addMsMember",
      text: "Add",
      iconProps: { iconName: "Add" },
      onClick: (): void => {
        setIsAddingUser(true);
        setIsPanelOpen(true);
      },
    },
  ];

  const handlePanelClick = (email: string): void => {
    if (MICROSOFT_DOMAIN_REGEX.test(email) && !!userDetails) {
      setSelectedEmail(email);
      if (!!userDetails) {
        dispatch(
          addMicrosoftMemberToDL(
            tenant,
            selectedDistributionList.distributionListEmail,
            userDetails.emailAddress,
            userDetails.text,
            userDetails.firstName,
            userDetails.lastName
          )
        );
      }
    } else {
      dispatch(
        removeMicrosoftMemberFromDL(
          tenant,
          selectedDistributionList.distributionListEmail,
          selectedEmail
        )
      );
    }
  };

  /**
   * Save changes to add/remove a user from a DL.
   */
  const save = (): void => {
    if (!!userDetails?.emailAddress) {
      return handlePanelClick(userDetails.emailAddress);
    } else if (isRemovingUser && selectedEmail) {
      return handlePanelClick(selectedEmail);
    }
  };

  /**
   * Search AAD User Info by the input user email.
   * @param {string}  filterText  the user email.
   * @param {IPersonaProps[] | undefined}  currentPersonas  Already selected persons.
   * @returns {IPersonaPropsExtend[] | Promise<IPersonaPropsExtend[]>} Selectable persons.
   */
  const onFilterChanged = (
    filterText: string,
    currentPersonas: IPersonaProps[] | undefined
  ): IPersonaPropsExtend[] | Promise<IPersonaPropsExtend[]> => {
    if (filterText && filterText.endsWith("@microsoft.com")) {
      return callMsGraphGetUserByEmail(filterText).then((value) => {
        if (!value.error && !currentPersonas?.find((p) => p.id === value.id)) {
          return [
            {
              id: value.id,
              imageInitials: `${value.displayName}`,
              text: value.displayName,
              secondaryText: value.userPrincipalName,
              presence: PersonaPresence.none,
              firstName: value.givenName,
              lastName: value.surname,
              emailAddress: value.userPrincipalName,
            },
          ];
        }
      }) as Promise<IPersonaPropsExtend[]>;
    } else {
      return [];
    }
  };

  const defaultSelect = [] as Array<IPersonaPropsExtend>;

  /**
   * Render item with secondary text for the NormalPeoplePicker.
   */
  const renderItemWithSecondaryText = (props: IPickerItemProps<IPersonaProps>): JSX.Element => {
    const newProps = {
      ...props,
      item: {
        ...props.item,
        ValidationState: ValidationState.valid,
        showSecondaryText: true,
      },
    };

    return <PeoplePickerItem {...newProps} />;
  };

  /**
   * The Validation for the access user form.
   */
  const addMSMemberSchema = Yup.object().shape({
    email: Yup.string()
      .test("Validate Email", "Email must be a Microsoft email", (value) => {
        return MICROSOFT_DOMAIN_REGEX.test(String(value).toLowerCase());
      })
      .notOneOf(
        microsoftMembers.map((ms) => ms.email),
        "This user is already a member of this group."
      ),
  });

  /**
   * The Validation for the access user form.
   */
  const removeMSMemberSchema = Yup.object().shape({
    email: Yup.string().notOneOf(
      microsoftMembers.map((ms) => ms.email),
      "This user is already a member of this group."
    ),
  });

  return (
    <>
      <Stack styles={wrapperStackStyles}>
        <Stack>
          <ActionButton
            iconProps={{ iconName: "RevToggleKey" }}
            ariaLabel="Back"
            onClick={onBackClickEvent}
          >
            Back
          </ActionButton>
        </Stack>

        <Stack tokens={containerStackTokens}>
          <PageHeader>
            <Title>{selectedDistributionList.distributionGroupDisplayName}</Title>
            <Subtitle>{selectedDistributionList.distributionListEmail}</Subtitle>
          </PageHeader>
          <ContentContainer>
            {canUpdateDistributionList && (
              <Stack>
                <CommandBar
                  styles={commandBarStyles}
                  items={items}
                  ariaLabel="Add microsoft member"
                />
                {successMessage && (
                  <MessageBar messageBarType={MessageBarType.success}>{successMessage}</MessageBar>
                )}
              </Stack>
            )}
            <ContainerWrapper>
              <Stack styles={headerStackStyles}>
                <Text styles={headerTextStyles}>Microsoft Members</Text>
              </Stack>
              <Stack styles={bodyContainerStackStyles}>
                <MicrosoftMembersList
                  canRemoveUser={canUpdateDistributionList}
                  setIsPanelOpen={setIsPanelOpen}
                  setIsAddingUser={setIsAddingUser}
                  setIsRemovingUser={setIsRemovingUser}
                  setSelectedEmail={setSelectedEmail}
                />
              </Stack>
            </ContainerWrapper>
          </ContentContainer>
        </Stack>
        <SidePanel
          isOpen={isPanelOpen}
          title={isAddingUser ? "Add a member" : "Remove a member"}
          saveButtonLabel={isAddingUser ? "Add" : "Remove"}
          error={addUserErrorMessage || removeUserErrorMessage}
          disableActions={isAddingOrRemoving}
          onDismiss={dismissPanel}
          onSave={(): void => save()}
          hideFooter
        >
          <FlexWrapper>
            <div>
              <PanelSubHeader>
                {selectedDistributionList.distributionGroupDisplayName}
              </PanelSubHeader>
              <p>
                {isAddingUser
                  ? "You can add a Microsoft FTE or external staff member to this distribution list."
                  : "You can remove this member from the DL. Removed users will not be receiving any further emails."}
              </p>
            </div>
            <Formik
              initialValues={{
                email: undefined,
              }}
              validationSchema={isAddingUser ? addMSMemberSchema : removeMSMemberSchema}
              onSubmit={(): void => setIsSubmitting(true)}
            >
              {({ setFieldValue, isValid, errors }): React.ReactNode => {
                return (
                  <StyledForm>
                    <Stack tokens={verticalStackTokens} styles={formStackStyles}>
                      <Stack
                        horizontal
                        wrap={true}
                        tokens={horizontalStackTokens}
                        styles={flexStackStyles}
                      >
                        {isAddingUser && (
                          <Stack.Item styles={fieldItemStyles}>
                            <Label>Email: </Label>
                            <Field
                              disabled={isAddingOrRemoving}
                              component={NormalPeoplePicker}
                              id="email"
                              label="Email Address"
                              name="email"
                              placeholder="Enter here"
                              defaultSelectedItems={defaultSelect}
                              onChange={(emailUser: IUserDetailsGraphProps[] | undefined): void => {
                                const userInfo =
                                  !!emailUser && emailUser.length > 0 ? emailUser[0] : undefined;
                                setUserDetails(userInfo);
                                setFieldValue("email", userInfo?.emailAddress);
                              }}
                              onResolveSuggestions={onFilterChanged}
                              pickerSuggestionsProps={peoplePickerSuggestionProps}
                              className="ms-PeoplePicker"
                              key="normal"
                              onRenderItem={renderItemWithSecondaryText}
                              removeButtonAriaLabel="Remove"
                              inputProps={{
                                "aria-label": "People Picker",
                              }}
                              componentRef={picker}
                              onInputChange={onInputChange}
                              resolveDelay={300}
                              itemLimit={1}
                            />
                            <FormikErrorMessage name="email" />
                          </Stack.Item>
                        )}
                      </Stack>
                    </Stack>
                    <Stack horizontal>
                      <Stack.Item>
                        {errors.email ? (
                          <MessageBar
                            messageBarType={MessageBarType.warning}
                            styles={messageBarStyles}
                          >
                            {errors.email}
                          </MessageBar>
                        ) : null}
                        {!!addUserErrorMessage ? (
                          <MessageBar
                            messageBarType={MessageBarType.warning}
                            styles={messageBarStyles}
                          >
                            {addUserErrorMessage}
                          </MessageBar>
                        ) : null}
                        {!!removeUserErrorMessage ? (
                          <MessageBar
                            messageBarType={MessageBarType.warning}
                            styles={messageBarStyles}
                          >
                            {removeUserErrorMessage}
                          </MessageBar>
                        ) : null}
                        <div>
                          <Separator />
                          <PrimaryButton
                            onClick={(): void => {
                              save();
                            }}
                            styles={footerButtonStyles}
                            disabled={!isValid || isSubmitting}
                            type="submit"
                          >
                            {isAddingUser ? "Add" : "Remove"}
                          </PrimaryButton>
                          <DefaultButton
                            onClick={dismissPanel}
                            styles={footerButtonStyles}
                            disabled={!isValid || isSubmitting}
                          >
                            Cancel
                          </DefaultButton>
                        </div>
                      </Stack.Item>
                    </Stack>
                  </StyledForm>
                );
              }}
            </Formik>
          </FlexWrapper>
        </SidePanel>
      </Stack>
    </>
  );
};

export default DistributionListDetails;
